import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import FilterGroup from "@/components/feature/FilterGroup";
import type { VideoItem, VideoType } from "@/types/video";
import { regions, years } from "@/mocks/videos";
import { getVideosByType } from "@/utils/video";

interface VideoListPageProps {
  type: VideoType;
  title: string;
  intro: string;
  categories: string[];
}

const sortOptions = [
  { key: "latest", label: "最新上线" },
  { key: "hot", label: "热门" },
  { key: "rating", label: "评分" },
] as const;

type SortKey = (typeof sortOptions)[number]["key"];

const PAGE_SIZE = 12;

function matchYear(itemYear: number, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return selected.some((value) => {
    if (value === "更早") return itemYear < 2020;
    return itemYear === Number(value);
  });
}

export default function VideoListPage({ type, title, intro, categories }: VideoListPageProps) {
  const allItems = useMemo(() => getVideosByType(type), [type]);
  const allRegions = useMemo(() => regions, []);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [yearFilter, setYearFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, regionFilter, yearFilter, sort]);

  const filtered = useMemo(() => {
    const result = allItems.filter((item: VideoItem) => {
      const matchCategory = categoryFilter.length === 0 || categoryFilter.includes(item.category);
      const matchRegion = regionFilter.length === 0 || regionFilter.includes(item.region);
      const matchY = matchYear(item.year, yearFilter);
      return matchCategory && matchRegion && matchY;
    });

    return result.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      return b.year - a.year || b.id - a.id;
    });
  }, [allItems, categoryFilter, regionFilter, yearFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggle = (list: string[], setList: (next: string[]) => void, value: string) => {
    setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const resetAll = () => {
    setCategoryFilter([]);
    setRegionFilter([]);
    setYearFilter([]);
    setSort("latest");
  };

  const hasFilter = categoryFilter.length > 0 || regionFilter.length > 0 || yearFilter.length > 0;

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-4" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
        <span className="text-foreground-800">{title}</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{title}</h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl">{intro}</p>
      </header>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <aside className="w-full lg:w-60 lg:shrink-0">
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-filter-3-line"></i></span>
              筛选条件
            </span>
            <span className={`w-5 h-5 flex items-center justify-center transition-transform ${filterOpen ? "rotate-180" : ""}`}>
              <i className="ri-arrow-down-s-line text-lg"></i>
            </span>
          </button>

          <div className={`${filterOpen ? "block" : "hidden"} lg:block mt-4 lg:mt-0 lg:sticky lg:top-24 rounded-lg bg-background-100 border border-background-200 p-4 md:p-5`}>
            <div className="hidden lg:flex items-center gap-2 mb-4">
              <span className="w-5 h-5 flex items-center justify-center text-primary-600"><i className="ri-filter-3-line"></i></span>
              <h2 className="text-sm font-semibold text-foreground-900">筛选</h2>
            </div>
            <FilterGroup title="类型" options={categories} selected={categoryFilter} onToggle={(v) => toggle(categoryFilter, setCategoryFilter, v)} onClear={() => setCategoryFilter([])} />
            <FilterGroup title="地区" options={allRegions} selected={regionFilter} onToggle={(v) => toggle(regionFilter, setRegionFilter, v)} onClear={() => setRegionFilter([])} />
            <FilterGroup title="年份" options={years} selected={yearFilter} onToggle={(v) => toggle(yearFilter, setYearFilter, v)} onClear={() => setYearFilter([])} />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option.key ? "bg-primary-500 text-white" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {pageItems.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground-900">没有找到符合条件的作品</h3>
              <p className="mt-2 text-sm text-foreground-500">换个筛选条件试试，或者一键重置全部筛选。</p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 px-5 py-2.5 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}

          {hasFilter && pageItems.length > 0 && (
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={resetAll}
                className="text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer"
              >
                重置全部筛选
              </button>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-8">
              <Pagination currentPage={safePage} totalPages={totalPages} onChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}